import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6om1crlgirdouph2tig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-black text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Newsletter - Wider Column */}
          <div className="lg:col-span-5">
            <div className="mb-6">
              <img
                src="https://lunch4you.de/assets/logo_new-BUicn5Zg.png"
                alt="Lunch4You"
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <h3 className="text-4xl font-light mb-4">Stay Fresh</h3>
            <p className="text-gray-400 mb-6 text-lg">
              Get weekly menu updates and exclusive offers
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full bg-transparent border-b-2 border-white/30 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              {submitStatus === 'success' && (
                <p className="text-primary-400 text-sm">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  Thank you for subscribing!
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm">
                  <i className="ri-error-warning-line mr-1"></i>
                  Something went wrong. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <span>{isSubmitting ? 'Subscribing...' : 'Subscribe'}</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  About
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Careers
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Press
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              <li>
                <a href="#menu" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Menu
                </a>
              </li>
              <li>
                <a href="#corporate" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Corporate
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Catering
                </a>
              </li>
              <li>
                <a href="#plans" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                  Subscriptions
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Get in Touch</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <i className="ri-phone-line"></i>
                <span>+49 30 1234 5678</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-mail-line"></i>
                <span>hello@freshmeals.de</span>
              </li>
            </ul>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider mt-6">Locations</h4>
            <p className="text-gray-400 text-sm leading-relaxed">
              Serving major cities<br />
              across Germany
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Links */}
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                <i className="ri-facebook-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">
                <i className="ri-linkedin-line text-xl"></i>
              </a>
            </div>

            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © 2024 Lunch4You. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm underline cursor-pointer">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}